// Turning raw values into what a person reads. All of it on this side.
//
// The service reports quantities as exact decimal strings, moments as ISO-8601,
// and counts as numbers, and formats none of them (see backend
// commerce/json_values.py). This is the other half of that arrangement.
//
// The rule that shapes everything here: **a quantity is never parsed as a
// number to be displayed.** `parseFloat("29204.100000")` is 29204.099999999999
// on every engine, and a customer reconciling a portal against an invoice is
// the person who finds it. So grouping and trailing-zero trimming are done on
// the string, digit by digit, and the value that reaches the screen is the value
// the ledger holds.

import type { DayRange } from './acrossCustomers';
import type { Period } from './reads';

/** What an absent value reads as. An em dash, not a zero. */
export const ABSENT = '—';

/**
 * One exact quantity, grouped for reading, with trailing zeros trimmed.
 *
 * Purely textual: `"104.500000"` becomes `"104.5"` and `"28640.000000"` becomes
 * `"28,640"` without the value ever being a `number`.
 */
export function quantityText(value: string | null): string {
  if (value === null) {
    return ABSENT;
  }

  const [whole, fraction = ''] = value.split('.');
  const negative = whole.startsWith('-');
  const digits = negative ? whole.slice(1) : whole;
  const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const trimmed = fraction.replace(/0+$/, '');
  const sign = negative ? '-' : '';

  return trimmed ? `${sign}${grouped}.${trimmed}` : `${sign}${grouped}`;
}

/** A whole count, grouped. Counts are numbers on the wire and safely small. */
export function countText(value: number): string {
  return value.toLocaleString();
}

/**
 * A quantity as a `number`, for chart geometry only.
 *
 * The one place a decimal string becomes a double, because a bar's height is a
 * pixel measurement and a pixel is not an exact decimal. Never use the result
 * as a displayed figure — the chart's tooltip shows the original string.
 */
export function quantityAsPixelValue(value: string | null): number {
  return value === null ? 0 : Number.parseFloat(value);
}

/** The short month a month-start timestamp falls in, e.g. `Mar`. */
/**
 * The month a period names, for copy that used to say "this month".
 *
 * It said that because the screen could only ever show the current one.
 * Now that a month can be chosen, "this month" is true only by accident —
 * and wrong in exactly the case the selector exists for, which is somebody
 * looking deliberately at a month that is not this one.
 */
export function monthOf(period: Period): string {
  return monthLabel(period.start);
}


export function monthLabel(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    timeZone: 'UTC',
  });
}

/**
 * A UTC calendar day (`YYYY-MM-DD`) as a person reads it.
 *
 * Formatted in UTC on purpose: the day is the UTC day a cost roll-up is keyed
 * on, and formatting it in the reader's zone would move a day posted for the
 * 6th onto the 5th for anybody west of Greenwich.
 */
export function dayLabel(day: string, withYear = false): string {
  return new Date(`${day}T00:00:00Z`).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
    ...(withYear ? { year: 'numeric' as const } : {}),
  });
}

/**
 * The run of days a range covers, as a reader would write it down, with how
 * many days that is — "1 Oct – 7 Oct 2026 · 7 days". A single day is named
 * once rather than as a range from itself to itself.
 */
export function dayRangeLabel(range: DayRange): string {
  const days = range.days === 1 ? '1 day' : `${range.days} days`;
  if (range.first === range.last) {
    return `${dayLabel(range.first, true)} · ${days}`;
  }
  return `${dayLabel(range.first)} – ${dayLabel(range.last, true)} · ${days}`;
}

/**
 * A usage hour, as the hour it is attributed to.
 *
 * Rendered in UTC and labelled as such, because that is the hour the ledger
 * keyed the row under. Showing it in the reader's timezone would put a row at
 * 09:00 for one colleague and 14:00 for another, and neither would match the
 * hour a channel was told about.
 */
export function usageHourLabel(iso: string | null): string {
  if (iso === null) {
    return ABSENT;
  }
  const stamp = new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  });
  return `${stamp} UTC`;
}

/** How long ago a moment was, in the reader's words. */
export function relativeTime(iso: string | null, now: Date = new Date()): string {
  if (iso === null) {
    return ABSENT;
  }

  const seconds = (new Date(iso).getTime() - now.getTime()) / 1000;
  const format = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

  const scales: [Intl.RelativeTimeFormatUnit, number][] = [
    ['second', 60],
    ['minute', 60],
    ['hour', 24],
    ['day', 30],
    ['month', 12],
  ];

  let value = seconds;
  for (const [unit, limit] of scales) {
    if (Math.abs(value) < limit) {
      return format.format(Math.round(value), unit);
    }
    value /= limit;
  }
  return format.format(Math.round(value), 'year');
}

/**
 * A share of a whole, as a percentage, or an em dash where there is no whole.
 *
 * Zero of zero is not 0% and not 100% — it is a question with no answer, and a
 * portal that answered it anyway would show a brand-new account either a
 * perfect record or a total failure on the strength of no records at all.
 */
export function percentText(part: number, whole: number): string {
  return whole === 0 ? ABSENT : `${Math.round((part / whole) * 100)}%`;
}
